import { useMemo, useState } from "react";
import type { DefinitionReadiness } from "@keydials/api-client";
import {
  BuilderWheel,
  definitionToDialModel,
  resolveSelection,
  segmentNoun,
  toDialSelection,
  type BuilderSelection,
} from "@keydials/dial-workspace";
import { applyBuildAction, type BuildAction } from "../builderReducer";
import { BuildList } from "../BuildList";
import { builderCopy as copy, fill, nounValues } from "../copy";
import type { DraftSession, DraftSnapshot } from "../draftSession";
import { readinessView } from "../readiness";
import { ReadinessPill } from "../ReadinessPill";
import { SelectionPanel } from "../SelectionPanel";

interface BuildStageProps {
  session: DraftSession;
  snapshot: DraftSnapshot;
  readiness: DefinitionReadiness;
  /** What to select on arrival: the section or action point "Fix this" pointed at. */
  initialSelection?: BuilderSelection | null;
  /** A problem the readiness pill points at on a field, which is fixed on Details. */
  onFix: (selection: BuilderSelection) => void;
  onNext: () => void;
}

/**
 * Build (KEY-560): the wheel is the editor. Tap a section or an action point to select it and edit it in the panel beside the
 * wheel; add what's missing from the ghost wedges or the panel. Every change goes through the shared structure edits and is
 * autosaved, and the readiness pill says what's left before the dial can go live. On a phone the panel becomes a list of the
 * sections, under the wheel as a thumbnail.
 */
export function BuildStage({ session, snapshot, readiness, initialSelection, onFix, onNext }: BuildStageProps) {
  const model = snapshot.model;
  const [selection, setSelection] = useState<BuilderSelection | null>(initialSelection ?? null);
  const locked = snapshot.lockedLifecycle !== null;

  const noun = segmentNoun(snapshot.noun ? { segmentNounSingular: snapshot.noun.singular, segmentNounPlural: snapshot.noun.plural } : null);
  const words = nounValues(noun);
  const dialModel = useMemo(() => definitionToDialModel(model, model.customPalettes ?? []), [model]);
  // A selection whose node has gone (removed, or undone by a reload) falls back to what's still there.
  const resolved = resolveSelection(model, selection);
  const current = resolved?.selection ?? null;

  function dispatch(action: BuildAction) {
    if (locked) return;
    let next: BuilderSelection | null = current;
    session.edit((draft) => {
      const outcome = applyBuildAction(draft, current, action);
      next = outcome.selection;
      return outcome.model;
    });
    setSelection(next);
  }

  const panelProps = { model, resolved, noun, disabled: locked, dispatch, onSelect: setSelection };

  return (
    <section className="kd-build" aria-labelledby="kd-builder-question">
      <div className="kd-build-head">
        <h1 id="kd-builder-question" className="kd-builder-question">
          {copy.build.prompt}
        </h1>
        <ReadinessPill view={readinessView(readiness, model, noun)} onJump={(jump) => (jump.level === "field" ? onFix(jump) : setSelection(jump))} />
      </div>
      <div className="kd-build-grid">
        <div className="kd-build-wheel">
          <BuilderWheel
            model={dialModel}
            selection={toDialSelection(current)}
            segmentWord={noun.one}
            labels={{ addFirstSection: copy.build.addFirstSection, addSegment: fill(copy.build.ghostAddSegment, words) }}
            ariaLabel={model.name}
            onSelectSection={(sectionKey) => setSelection({ level: "section", sectionKey })}
            onSelectSegment={(sectionKey, segmentKey) => setSelection({ level: "segment", sectionKey, segmentKey })}
            onAddFirstSection={() => dispatch({ type: "addSection" })}
            onAddSegment={(sectionKey) => dispatch({ type: "addSegment", sectionKey })}
          />
        </div>
        <div className="kd-build-side">
          <SelectionPanel {...panelProps} />
          {model.sections.length > 0 && (
            <div className="kd-panel-actions kd-build-add-section">
              <button type="button" className="kd-btn" disabled={locked} onClick={() => dispatch({ type: "addSection" })}>
                {copy.build.addSection}
              </button>
            </div>
          )}
        </div>
        <BuildList {...panelProps} />
      </div>
      <div className="kd-builder-footer">
        <button type="button" className="kd-btn-primary" onClick={onNext}>
          {copy.frame.continue}
        </button>
      </div>
    </section>
  );
}
